import { Box, Typography, Tooltip, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  CircleTickIcon,
  CircleWarningIcon,
  EditIcon,
  InlineStepIcon,
} from "../../components-new/icons";
import { EllipsisTooltipText } from "../../components-new/common";
import { getStepStatus } from "../../utils/helpers";
import { formatDuration } from "../../../src/utils/timeFormatter.js";
import { useAuthedMediaSrc } from "../../hooks";

const PLACEHOLDER_IMAGE = "/_No_Image__placeholder.png";

const ActionButton = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 88px;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const styles = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const getConditionalLogicStatus = (item) => {
  if (!item?.has_conditional_logic) return null;

  const baseInfo = {
    condLogic: item.condLogic || "N/A",
    condExp: item.condExp || "N/A",
    evaluatedValue: item.evaluatedValue,
  };

  if (item.was_evaluated && item.was_executed) {
    return {
      label: "Executed",
      color: "#2e7d32",
      tooltip: "Conditional logic was evaluated and used in step execution",
      ...baseInfo,
    };
  } else if (item.was_evaluated && !item.was_executed) {
    return {
      label: "Skipped",
      color: "#ed6c02",
      tooltip: "Conditional logic was evaluated but not used in step execution",
      ...baseInfo,
    };
  } else {
    return {
      label: "Not Evaluated",
      color: "#9e9e9e",
      tooltip: "Conditional logic was not evaluated",
      ...baseInfo,
    };
  }
};

const getConfidenceArrow = (result) => {
  const value = result?.toLowerCase() ?? "";
  if (value.startsWith("increase"))
    return <ArrowUpwardIcon sx={{ fontSize: 20, color: "green" }} />;
  if (value.startsWith("decrease"))
    return <ArrowDownwardIcon sx={{ fontSize: 20, color: "red" }} />;
  if (value.startsWith("equal"))
    return <RemoveIcon sx={{ fontSize: 20, color: "#8089A8" }} />;
  return null;
};

const TestItemCard = ({
  item,
  handleEdit,
  handleViewDesignIssues,
  designPrincipals,
  index,
  width,
}) => {
  const clStatus = getConditionalLogicStatus(item);
  const stepStatus = getStepStatus(item);
  const { src: screenshotSrc } = useAuthedMediaSrc(item?.Failure_Screenshot_Location, {
    fallback: PLACEHOLDER_IMAGE,
  });
  return (
    <Box
      flex={1}
      minWidth={`${width}px`}
      maxWidth={`${width}px`}
      gap={"8px"}
      alignItems={"flex-start"}
      padding={"14px"}
      display={"flex"}
      border={`2px ${
        item.Keyword === "Inline"
          ? "dashed #6366F1"
          : item.divergentStep
            ? "solid #F59E0B"
            : "solid #ffffff"
      }`}
      borderRadius={"6px"}
      bgcolor={item.Keyword === "Inline" ? "#F6F8FB" : item.divergentStep ? "#FFFBEB" : "#ffffff"}
      boxShadow={"0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)"}
      flexDirection={"column"}
    >
      <Box
        borderRadius={"4px"}
        height={220}
        width={"100%"}
        border={"1px solid rgba(0, 0, 0, 0.30)"}
        sx={styles}
      >
        {item?.Keyword === "Inline" ? (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <InlineStepIcon />

            <Typography variant="body2" sx={{ color: "#8089A8" }} fontWeight={700} maxWidth={"80%"}>
              {item?.TestData}
            </Typography>
          </div>
        ) : (
          <img
            style={{ height: "100%", width: "100%" }}
            src={screenshotSrc || PLACEHOLDER_IMAGE}
            alt="testImage"
          />
        )}
      </Box>

      {item.Keyword !== "Inline" && (
        <Box width={"100%"} display={"flex"} justifyContent="space-between" alignItems={"center"}>
          <Box display={"flex"} columnGap={"8px"}>
            <Typography>{item?.Step_Number}</Typography>
            {/* Only show an icon for a known status - a missing one must never read as a pass */}
            {stepStatus === "PASS" && <CircleTickIcon />}
            {stepStatus === "FAIL" && <CircleWarningIcon />}
          </Box>
          {/* Middle: Similarity with color */}
          {item?.Step_Number > 1 && item?.similarity != null && (
            <Typography
              variant="body1"
              sx={{
                color:
                  item.similarity === 100 ? "green" : item.similarity > 95 ? "goldenrod" : "red",
                fontWeight: 500, // match Step_Number if needed
              }}
            >
              {item.similarity.toFixed(1)}%
            </Typography>
          )}
          {item?.Status !== "STARTED" && item?.Status !== "ABORTED" && (
            <Box display={"flex"} columnGap={"15px"} alignItems={"center"}>
              <Tooltip title="Step Execution Details">
                <ActionButton border={"2px solid #547de8"} onClick={() => handleEdit(item)}>
                  <EditIcon />
                </ActionButton>
              </Tooltip>
              {designPrincipals && (
                <Tooltip title="View visual analysis results">
                  <ActionButton
                    border={"2px solid #547de8"}
                    onClick={() => handleViewDesignIssues && handleViewDesignIssues(item)}
                  >
                    <VisibilityIcon sx={{ color: "#547de8", fontSize: "20px" }} />
                  </ActionButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      )}
      <Box
        width={"100%"}
        rowGap={1}
        columnGap={2}
        display={"flex"}
        flexWrap={"wrap"}
        justifyContent={"space-between"}
      >
        <Box>
          <Typography fontWeight={"bold"}>Status</Typography>
          <Typography
            sx={{
              color: stepStatus === "PASS" ? "green" : "red",
            }}
          >
            {stepStatus ? stepStatus : "-"}
          </Typography>
        </Box>
        <Box textAlign={"right"}>
          <Typography fontWeight={"bold"}>Duration</Typography>
          <Typography>
            {formatDuration(item?.test_step_duration ? item?.test_step_duration : "-")}
          </Typography>
        </Box>
      </Box>
      {clStatus && (
        <Box width="100%">
          <Typography fontWeight="bold">Conditional Logic</Typography>
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                  {clStatus.tooltip}
                </Typography>
                <Box component="table" sx={{ "& td": { pr: 2, py: 0.25 } }}>
                  <tbody>
                    <tr>
                      <td>
                        <Typography variant="caption" sx={{ color: "#e2e8f0" }}>
                          Logic Type:
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="caption">{clStatus.condLogic}</Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography variant="caption" sx={{ color: "#e2e8f0" }}>
                          Expression:
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                          {clStatus.condExp}
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography variant="caption" sx={{ color: "#e2e8f0" }}>
                          Evaluated Value:
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="caption">
                          {clStatus.evaluatedValue !== undefined
                            ? String(clStatus.evaluatedValue)
                            : "N/A"}
                        </Typography>
                      </td>
                    </tr>
                  </tbody>
                </Box>
              </Box>
            }
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "#1e293b",
                  "& .MuiTooltip-arrow": {
                    color: "#1e293b",
                  },
                },
              },
            }}
          >
            <Typography
              sx={{
                color: clStatus.color,
                fontWeight: 500,
                cursor: "help",
                display: "inline-block",
              }}
            >
              {clStatus.label}
            </Typography>
          </Tooltip>
        </Box>
      )}
      {item.self_healed && (
        <Box width="100%" mt={1}>
          <Typography fontWeight="bold" mb={0.5}>
            Self Healed
          </Typography>
          <Chip
            icon={<AutoFixHighIcon style={{ fontSize: "16px" }} />}
            label={`Healed from: ${item.self_healed_from}`}
            color="warning"
            variant="outlined"
            size="small"
            sx={{ maxWidth: "100%" }}
          />
        </Box>
      )}
      {item.Keyword !== "Inline" && (
        <Box
          width={"100%"}
          rowGap={1}
          columnGap={2}
          display={"flex"}
          flexWrap={"wrap"}
          justifyContent={"space-between"}
        >
          <Box>
            <Typography fontWeight={"bold"}>Keyword</Typography>
            <Typography>{item?.Keyword ? item?.Keyword : "-"}</Typography>
          </Box>
          <Box textAlign={"right"}>
            <Typography fontWeight={"bold"}>Action</Typography>
            <Typography>{item?.Action ? item?.Action : "-"}</Typography>
          </Box>
        </Box>
      )}

      {item.Keyword !== "Inline" && (
        <Box width={"100%"}>
          <Typography fontWeight={"bold"}>Confidence Score</Typography>
          <Box display="flex" alignItems="center" columnGap={0.5}>
            <Typography>
              {item?.confidence_score != null ? `${item.confidence_score}%` : "N/A"}
            </Typography>
            {getConfidenceArrow(item?.confidence_score_result)}
          </Box>
        </Box>
      )}

      {item.Keyword !== "Inline" && Number(item?.centreX) > 0 && (
        <Box width={"100%"}>
          <Typography fontWeight={"bold"}>Centre X</Typography>
          <Typography>{item.centreX}</Typography>
        </Box>
      )}

      {item.Keyword !== "Inline" && Number(item?.centreY) > 0 && (
        <Box width={"100%"}>
          <Typography fontWeight={"bold"}>Centre Y</Typography>
          <Typography>{item.centreY}</Typography>
        </Box>
      )}

      {(item?.Keyword === "Assertion" || item?.Keyword === "visualAnalysis") &&
        item?.assertion_message && (
          <Tooltip title={item.assertion_message} arrow placement="top">
            <Chip
              label={item.assertion_message}
              size="small"
              variant="outlined"
              color={stepStatus === "PASS" ? "success" : "error"}
              sx={{
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          </Tooltip>
        )}
      {!(item?.assertion_message && !item?.TestData) && (
        <Box>
          <Box flex={1}>
            <Typography fontWeight={"bold"}>Test Data</Typography>
            <Typography sx={{ overflowWrap: "anywhere" }}>
              <EllipsisTooltipText
                text={item.TestData ? item.TestData : "-"}
                maxChars={80}
                mode="chars"
              />
            </Typography>
          </Box>
        </Box>
      )}
      {stepStatus === "FAIL" && (
        <>
          <Box>
            <Box flex={1}>
              <Typography fontWeight={"bold"}>Issue Type</Typography>
              <Typography sx={{ overflowWrap: "anywhere" }}>
                <EllipsisTooltipText
                  text={item.test_step_issuetype ? item.test_step_issuetype : "-"}
                  maxChars={80}
                  mode="chars"
                />
              </Typography>
            </Box>
          </Box>
          <Box>
            <Box flex={1}>
              <Typography fontWeight={"bold"}>Issues</Typography>
              <Typography sx={{ overflowWrap: "anywhere" }}>
                <EllipsisTooltipText
                  text={item.test_step_issues ? item.test_step_issues : "-"}
                  maxChars={80}
                  mode="chars"
                />
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TestItemCard;
